import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthState, User, AuthResponse, LoginRequest, RegisterRequest } from '@/types'
import { authApi, userApi } from '@/services/api'

interface AuthActions {
  login: (credentials: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<boolean>
  updateUser: (user: Partial<User>) => void
  clearError: () => void
  /** Set user after password reset (cookies already set by backend). */
  setUserFromPasswordReset: (user: User) => void
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (credentials: LoginRequest) => {
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false, 
          isLoading: true, 
          error: null 
        })
        
        try {
          const response = await authApi.login(credentials)
          const { user } = response.data.data
          
          // Small delay to ensure cookies are set before next requests
          await new Promise(resolve => setTimeout(resolve, 50))
          
          set({
            user,
            token: 'cookie-based', // Tokens are now in HTTP-only cookies
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error: any) {
          // Extract error message from API response
          const errorMessage = error.response?.data?.error || 
                              error.response?.data?.message || 
                              error.message || 
                              'Login failed'
          set({
            isLoading: false,
            error: errorMessage
          })
          throw error
        }
      },

      register: async (data: RegisterRequest) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await authApi.register(data)
          const { user } = response.data.data
          
          set({
            user,
            token: 'cookie-based', // Tokens are now in HTTP-only cookies
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error: any) {
          // Extract error message from API response
          const errorMessage = error.response?.data?.error || 
                              error.response?.data?.message || 
                              error.message || 
                              'Registration failed'
          set({
            isLoading: false,
            error: errorMessage
          })
          throw error
        }
      },

      logout: async () => {
        try {
          await authApi.logout()
        } catch (error) {
          // Continue with logout even if API call fails
        }
        
        // Clear localStorage
        localStorage.removeItem('djelo-auth')
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null
        })
      },

      checkAuth: async (): Promise<boolean> => {
        set({ isLoading: true })
        
        try {
          const response = await userApi.getProfile()
          
          // Check if data is nested or direct
          const user = response.data.data || response.data
          
          set({
            user,
            token: 'cookie-based',
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
          
          return true
        } catch (error: any) {
          // User is not authenticated or cookies expired
          // This is normal when no user is logged in
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          })
          
          return false
        }
      },


      updateUser: (userData: Partial<User>) => {
        const { user } = get()
        if (user) {
          set({ user: { ...user, ...userData } })
        }
      },

      clearError: () => {
        set({ error: null })
      },

      setUserFromPasswordReset: (user: User) => {
        set({
          user,
          token: 'cookie-based',
          isAuthenticated: true,
          error: null,
        })
      }
    }),
    {
      name: 'djelo-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)
